/** REST client for helm-api (SPEC §5.1 `HTTPS /v1/*`). Cookie-authenticated; same origin. */
import type { CopyConfig, CopyLogRow, CopyView } from "../copy/model";

/** How a session signed in. A PIN session cannot change security settings (ADR-0035). */
export type AuthMethod = "magic_link" | "passkey" | "pin" | "password";

export type Me = {
  user_id: string;
  email: string;
  device_id: string | null;
  /** Absent on an older server: treat as a full (non-PIN) session. */
  auth_method?: AuthMethod;
  /** A passkey was proven in this session in the last few minutes. */
  reverified_recently?: boolean;
  /** The owner (`HELM_OWNER_EMAIL`): Settings → Users (ADR-0051). */
  is_admin?: boolean;
};
export type Meta = {
  server_mode: string;
  version: string;
  /** Passkeys are configured on the server (helm-api has HELM_PUBLIC_ORIGIN(S)). */
  passkeys?: boolean;
  /** Passkeys work at the address this page is on (it is one of the configured origins). */
  passkeys_here?: boolean;
  /** "Sign in with PIN" is offered (Personal mode). */
  pin_sign_in?: boolean;
  /** Unapproved devices are refused (HELM_REQUIRE_DEVICE_APPROVAL). */
  device_approval_required?: boolean;
  /** Email + password sign-in is offered (ADR-0051). Absent on an older server. */
  password_sign_in?: boolean;
  /** HELM can email codes (new devices, "Forgot password"). */
  email_codes?: boolean;
};

export type DeviceRecord = {
  id: string;
  platform: string;
  name: string;
  approved: boolean;
  revoked: boolean;
  last_seen_at: string | null;
  created_at: string;
  /** Remembered for password sign-in until then (ADR-0051); absent / null when not. */
  trusted_until?: string | null;
};

/** `POST /v1/auth/password`: signed in (remembered device) or a code was emailed. */
export type PasswordSignInResponse =
  | { status: "signed_in" }
  | { status: "code_required"; challenge: string; expires_in_secs: number; resend_after_secs: number };

/** `POST /v1/auth/password/code`: signed in on this (now approved) device. */
export type CodeSignInResponse = {
  status: "signed_in";
  device: DeviceRecord;
  /** Only for a new device the browser asked to remember: keep it to re-attach later. */
  device_secret?: string | null;
};

/** Settings → Security: the password (never the password itself). */
export type PasswordView = {
  set: boolean;
  updated_at: string | null;
  /** Only while sign-in is locked after wrong tries. */
  locked_until: string | null;
  /** This session may change it (not a PIN session, approved device). */
  can_change: boolean;
  /** HELM can email a code (instead of the current password). */
  email_codes: boolean;
  /** A passkey was proven moments ago: enough on its own. */
  passkey_fresh: boolean;
};

/** Settings → Users (the owner only). */
export type UserRecord = {
  id: string;
  email: string;
  is_owner: boolean;
  created_at: string;
  invited_at: string | null;
  password_set: boolean;
  disabled_at: string | null;
};
export type UsersView = { users: UserRecord[]; guests_enabled: boolean; email_delivery: boolean };

/** What the browser tells the code step about itself (a device it registered earlier). */
export type DeviceHint = { platform: string; name: string; device_id?: string; device_secret?: string };

export type RegisterDeviceResponse = {
  device: DeviceRecord;
  approval: "approved" | "code_required" | "trusted_device_required";
  /** Only on a fresh registration. */
  device_secret?: string;
};

export type PasskeyRecord = {
  id: string;
  name: string;
  device_name: string | null;
  created_at: string;
  last_used_at: string | null;
};

/** A Rithmic gateway a login can be stored for (ADR-0027). */
export type RithmicServer = {
  id: string;
  name: string;
  environment: "test" | "paper" | "live";
  note: string;
  host: string;
};

/** A stored Rithmic login. There is no password field: the API never returns it (ADR-0027). */
export type RithmicLoginRecord = {
  id: string;
  label: string;
  server_id: string;
  environment: "test" | "paper" | "live";
  system_name: string;
  username: string;
  trading_enabled: boolean;
  trading_changed_at: string | null;
  created_at: string;
  /** Reconnects by itself after a HELM server restart (the login last connected; gateway-written). */
  auto_connect: boolean;
};

export type NewRithmicLogin = {
  server_id: string;
  system_name: string;
  username: string;
  password: string;
  label?: string;
};

/** Settings → Security: the sign-in PIN (never the PIN itself, ADR-0035). */
export type PinView = {
  set: boolean;
  updated_at: string | null;
  /** Only while PIN sign-in is locked after wrong PINs. */
  locked_until: string | null;
  /** This session may set / change / remove it (not a PIN session, approved device). */
  can_change: boolean;
  /** Prove a passkey first (the account has passkeys). */
  reverify_needed: boolean;
};

/** One line of sign-in / security activity (Settings → Devices). */
export type ActivityRecord = {
  id: number;
  at: string;
  action: string;
  method: string | null;
  ip: string | null;
};

/** A WebAuthn challenge paired with its server-side ceremony. */
export type Challenge<T> = { ceremony_id: string; options: T };

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

const deviceNotApprovedListeners = new Set<() => void>();

/** Called whenever any request is refused with 403 DEVICE_NOT_APPROVED. Returns an unsubscribe. */
export function onDeviceNotApproved(cb: () => void): () => void {
  deviceNotApprovedListeners.add(cb);
  return () => {
    deviceNotApprovedListeners.delete(cb);
  };
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  const res = await fetch(path, { credentials: "same-origin", ...init, headers });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const json: unknown = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const err = json as { code?: string; message?: string } | null;
    if (err?.code === "DEVICE_NOT_APPROVED") for (const cb of deviceNotApprovedListeners) cb();
    throw new ApiError(res.status, err?.code ?? "HTTP", err?.message ?? res.statusText);
  }
  return json as T;
}

/** The fetch wrapper behind `api`, for `accountApi` (api/account.ts). */
export { request as apiRequest };

export const api = {
  meta: () => request<Meta>("/v1/meta"),
  /** Resolves to `null` when there is no session (401) instead of throwing. */
  me: async (): Promise<Me | null> => {
    try {
      return await request<Me>("/v1/auth/me");
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return null;
      throw e;
    }
  },
  requestMagicLink: (email: string) =>
    request<{ status: string }>("/v1/auth/magic-link", { method: "POST", body: JSON.stringify({ email }) }),
  logout: async (): Promise<void> => {
    await request<unknown>("/v1/auth/logout", { method: "POST" });
  },
  registerDevice: (body: { platform: string; name: string; device_id?: string; device_secret?: string }) =>
    request<RegisterDeviceResponse>("/v1/devices", { method: "POST", body: JSON.stringify(body) }),
  listDevices: () => request<DeviceRecord[]>("/v1/devices"),
  /** With `code`: this device approves itself with the first-device code. Without: approve another device. */
  approveDevice: (id: string, code?: string) =>
    request<undefined>(`/v1/devices/${encodeURIComponent(id)}/approve`, {
      method: "POST",
      body: JSON.stringify(code === undefined ? {} : { code }),
    }),
  revokeDevice: (id: string) =>
    request<undefined>(`/v1/devices/${encodeURIComponent(id)}`, { method: "DELETE" }),
  passkeyLoginStart: () => request<Challenge<unknown>>("/v1/auth/passkey/start", { method: "POST" }),
  passkeyLoginFinish: (ceremony_id: string, credential: unknown) =>
    request<{ status: string }>("/v1/auth/passkey/finish", {
      method: "POST",
      body: JSON.stringify({ ceremony_id, credential }),
    }),
  /** Email + PIN sign-in (Personal mode). Every refusal is the same 401 PIN_REFUSED. */
  pinSignIn: (email: string, pin: string) =>
    request<{ status: string }>("/v1/auth/pin", { method: "POST", body: JSON.stringify({ email, pin }) }),
  // Email + password sign-in (ADR-0051). Passwords and codes travel only in request bodies.
  /** Every refusal is the same 401 SIGNIN_REFUSED; a new device gets `code_required`. */
  passwordSignIn: (email: string, password: string) =>
    request<PasswordSignInResponse>("/v1/auth/password", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  pinStatus: () => request<PinView>("/v1/security/pin"),
  setPin: (pin: string) =>
    request<PinView>("/v1/security/pin", { method: "PUT", body: JSON.stringify({ pin }) }),
  removePin: () => request<undefined>("/v1/security/pin", { method: "DELETE" }),
  securityActivity: (limit = 30) => request<ActivityRecord[]>(`/v1/security/activity?limit=${String(limit)}`),
  /** Step-up: prove a passkey again in this session (before changing the PIN). */
  reverifyStart: () => request<Challenge<unknown>>("/v1/auth/reverify/start", { method: "POST" }),
  reverifyFinish: (ceremony_id: string, credential: unknown) =>
    request<undefined>("/v1/auth/reverify/finish", {
      method: "POST",
      body: JSON.stringify({ ceremony_id, credential }),
    }),
  listPasskeys: () => request<PasskeyRecord[]>("/v1/passkeys"),
  passkeyRegisterStart: () => request<Challenge<unknown>>("/v1/passkeys/register/start", { method: "POST" }),
  passkeyRegisterFinish: (ceremony_id: string, name: string, credential: unknown) =>
    request<PasskeyRecord>("/v1/passkeys/register/finish", {
      method: "POST",
      body: JSON.stringify({ ceremony_id, name, credential }),
    }),
  deletePasskey: (id: string) =>
    request<undefined>(`/v1/passkeys/${encodeURIComponent(id)}`, { method: "DELETE" }),
  gatewayToken: () => request<{ token: string; session_id: string }>("/v1/gateway/token", { method: "POST" }),
  // Rithmic logins (ADR-0027). The password travels only in the body of createRithmicLogin.
  rithmicServers: () => request<RithmicServer[]>("/v1/rithmic/servers"),
  rithmicSystemInfo: (server_id: string) =>
    request<{ system_names: string[] }>("/v1/rithmic/system-info", {
      method: "POST",
      body: JSON.stringify({ server_id }),
    }),
  rithmicLogins: () => request<RithmicLoginRecord[]>("/v1/rithmic/logins"),
  createRithmicLogin: (body: NewRithmicLogin) =>
    request<RithmicLoginRecord>("/v1/rithmic/logins", { method: "POST", body: JSON.stringify(body) }),
  deleteRithmicLogin: (id: string) =>
    request<undefined>(`/v1/rithmic/logins/${encodeURIComponent(id)}`, { method: "DELETE" }),
  testRithmicLogin: (id: string) =>
    request<{ ok: boolean; message: string }>(`/v1/rithmic/logins/${encodeURIComponent(id)}/test`, {
      method: "POST",
    }),
  /**
   * "Stop retrying" a refused login (owner decision 2026-10-04): the gateway ends its bounded
   * automatic retry, and the login no longer connects by itself after a restart. Audited.
   * `stopped: false` when nothing was scheduled any more.
   */
  stopRithmicRetry: (id: string) =>
    request<{ stopped: boolean }>(`/v1/rithmic/logins/${encodeURIComponent(id)}/stop-retry`, {
      method: "POST",
    }),
  // Copy trader (ADR-0037). Saving never turns copying on; `setCopyEnabled` is the only writer
  // that does (typed confirmation when a real account is involved). ADR-0052: `group_id` turns
  // that group ON or pauses it; off without one pauses every group ("Copy OFF — all groups").
  copyConfig: () => request<CopyView>("/v1/copy"),
  saveCopyConfig: (config: CopyConfig, expected_version: number | null = null) =>
    request<CopyView>("/v1/copy", { method: "PUT", body: JSON.stringify({ config, expected_version }) }),
  setCopyEnabled: (body: {
    enabled: boolean;
    group_id?: string;
    confirm_text?: string;
    acknowledge_real_accounts?: boolean;
  }) => request<CopyView>("/v1/copy/enabled", { method: "POST", body: JSON.stringify(body) }),
  copyLog: (limit = 200) => request<CopyLogRow[]>(`/v1/copy/log?limit=${String(limit)}`),
  /** The owner switch. Enabling needs the user name typed exactly and the acknowledgement. */
  setRithmicTrading: (
    id: string,
    body: { enabled: boolean; confirm_username?: string; acknowledge_real_account?: boolean },
  ) =>
    request<RithmicLoginRecord>(`/v1/rithmic/logins/${encodeURIComponent(id)}/trading`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  /**
   * HELM's stored daily history of one account (ADR-0047), oldest first; parse with
   * `parseDayRows` (tables/firmFigures.ts). Read-only: the gateway writes it.
   */
  dailyHistory: (accountId: string, from?: string) =>
    request<unknown>(
      `/v1/accounts/${encodeURIComponent(accountId)}/daily-history${from ? `?from=${encodeURIComponent(from)}` : ""}`,
    ),
};
